import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { guides, getDifficultyColor } from '../data/guides';

const gradientMap = {
  'from-blue-500 to-cyan-600':    'linear-gradient(135deg, #3b82f6, #0891b2)',
  'from-pink-500 to-rose-600':    'linear-gradient(135deg, #ec4899, #e11d48)',
  'from-red-500 to-orange-600':   'linear-gradient(135deg, #ef4444, #ea580c)',
  'from-violet-500 to-purple-600':'linear-gradient(135deg, #8b5cf6, #9333ea)',
  'from-amber-500 to-yellow-600': 'linear-gradient(135deg, #f59e0b, #ca8a04)',
  'from-green-500 to-emerald-600':'linear-gradient(135deg, #22c55e, #059669)',
  'from-blue-600 to-indigo-700':  'linear-gradient(135deg, #2563eb, #4338ca)',
  'from-rose-500 to-pink-600':    'linear-gradient(135deg, #f43f5e, #db2777)',
  'from-orange-500 to-amber-600': 'linear-gradient(135deg, #f97316, #d97706)',
  'from-teal-500 to-cyan-600':    'linear-gradient(135deg, #14b8a6, #0891b2)',
  'from-yellow-500 to-orange-600':'linear-gradient(135deg, #eab308, #ea580c)',
  'from-indigo-500 to-violet-600':'linear-gradient(135deg, #6366f1, #7c3aed)',
  'from-emerald-600 to-green-700':'linear-gradient(135deg, #059669, #15803d)',
};

const accentMap = {
  'from-blue-500 to-cyan-600':    '#3b82f6',
  'from-pink-500 to-rose-600':    '#ec4899',
  'from-red-500 to-orange-600':   '#ef4444',
  'from-violet-500 to-purple-600':'#8b5cf6',
  'from-amber-500 to-yellow-600': '#f59e0b',
  'from-green-500 to-emerald-600':'#22c55e',
  'from-blue-600 to-indigo-700':  '#2563eb',
  'from-rose-500 to-pink-600':    '#f43f5e',
  'from-orange-500 to-amber-600': '#f97316',
  'from-teal-500 to-cyan-600':    '#14b8a6',
  'from-yellow-500 to-orange-600':'#eab308',
  'from-indigo-500 to-violet-600':'#6366f1',
  'from-emerald-600 to-green-700':'#059669',
};

const getGradient = c => gradientMap[c] || 'linear-gradient(135deg, #6366f1, #a78bfa)';
const getAccent   = c => accentMap[c]   || '#818cf8';

const GuidesPage = () => {
  const [search, setSearch] = useState('');

  const filtered = guides.filter(g =>
    !search ||
    g.title.toLowerCase().includes(search.toLowerCase()) ||
    g.tags.some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="guides-page-premium">
      {/* Header Section */}
      <div className="guides-header-premium">
        <div className="guides-header-inner">
          <span className="guides-badge">
            <span className="guides-badge-dot"></span>
            {filtered.length} Premium Guides
          </span>
          <h1 className="guides-title-premium">
            System Design <span className="guides-title-accent">Guides</span>
          </h1>
          <p className="guides-subtitle-premium">
            Deep-dive walkthroughs of real-world system design problems.
            Master the patterns used by top tech companies.
          </p>
          
          {/* Search Bar */}
          <div className="guides-search-wrap">
            <div className="guides-search-icon">🔍</div>
            <input
              className="guides-search-premium"
              placeholder="Search by title, tag, or difficulty…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button 
                className="guides-search-clear"
                onClick={() => setSearch('')}
              >
                ✕
              </button>
            )}
          </div>

          {/* Stats Row */}
          <div className="guides-stats-row">
            <div className="guides-stat-item">
              <span className="stat-num">13</span>
              <span className="stat-label">Guides</span>
            </div>
            <div className="guides-stat-divider"></div>
            <div className="guides-stat-item">
              <span className="stat-num">3</span>
              <span className="stat-label">Levels</span>
            </div>
            <div className="guides-stat-divider"></div>
            <div className="guides-stat-item">
              <span className="stat-num">260+</span>
              <span className="stat-label">Min Content</span>
            </div>
          </div>
        </div>
      </div>

      {/* Grid Section */}
      {filtered.length === 0 ? (
        <div className="guides-empty-state">
          <div className="empty-icon">🔍</div>
          <p className="empty-text">No guides found for "{search}"</p>
          <button className="empty-clear" onClick={() => setSearch('')}>
            Clear search
          </button>
        </div>
      ) : (
        <div className="guides-grid-premium">
          {filtered.map((guide, index) => {
            const accent   = getAccent(guide.color);
            const gradient = getGradient(guide.color);
            const hasThumbnail = !!guide.thumbnail;
            const isHero = index === 0; // First card is hero

            return (
              <Link
                key={guide.slug}
                to={`/guides/${guide.slug}`}
                className={`guide-card-premium ${isHero ? 'guide-card-hero' : ''}`}
                style={{ 
                  '--accent': accent, 
                  '--gradient': gradient,
                  '--diff-color': getDifficultyColor(guide.difficulty)
                }}
              >
                {/* Thumbnail Container */}
                <div className="guide-thumb-premium">
                  {hasThumbnail ? (
                    <>
                      <img
                        src={guide.thumbnail}
                        alt={guide.title}
                        className="guide-thumb-img"
                        loading="lazy"
                      />
                      <div className="guide-thumb-overlay"></div>
                    </>
                  ) : (
                    <>
                      <div 
                        className="guide-thumb-gradient"
                        style={{ background: gradient }}
                      ></div>
                      <div className="guide-thumb-glow"></div>
                      <div className="guide-emoji-float">
                        {guide.emoji}
                      </div>
                    </>
                  )}
                  
                  {/* Floating Difficulty Badge */}
                  <div 
                    className="guide-difficulty-badge"
                    style={{ 
                      background: `${getDifficultyColor(guide.difficulty)}20`,
                      borderColor: `${getDifficultyColor(guide.difficulty)}50`,
                      color: getDifficultyColor(guide.difficulty)
                    }}
                  >
                    {guide.difficulty}
                  </div>
                </div>

                {/* Content Container */}
                <div className="guide-content-premium">
                  <div className="guide-content-main">
                    <h3 className="guide-title-card">{guide.title}</h3>
                    <p className="guide-description-card">{guide.description}</p>
                  </div>

                  {/* Meta Row */}
                  <div className="guide-meta-row">
                    <div className="guide-tags">
                      {guide.tags.slice(0, isHero ? 3 : 2).map(tag => (
                        <span key={tag} className="guide-tag-pill">{tag}</span>
                      ))}
                    </div>
                    <div className="guide-read-time">
                      <span className="time-icon">⏱</span>
                      <span>{guide.readTime}</span>
                    </div>
                  </div>
                </div>

                {/* Hover Overlay with CTA */}
                <div className="guide-hover-overlay">
                  <div className="hover-content">
                    <div className="hover-cta-btn">
                      <span>Read Guide</span>
                      <span className="hover-arrow">→</span>
                    </div>
                    <div className="hover-chapters">
                      {guide.chapters && guide.chapters.length > 0 && (
                        <>
                          <div className="chapters-label">
                            {guide.chapters.length} Chapters
                          </div>
                          <div className="chapters-preview">
                            {guide.chapters.slice(0, 3).map((ch, i) => (
                              <div key={i} className="chapter-dot"></div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Shimmer Effect */}
                <div className="guide-shimmer"></div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GuidesPage;
